import { createTranslator, isLocale, sourceLocale, type Translate } from '@trellistry/platform/i18n';
import { AppShell, type ShellLink } from '@trellistry/ui';
import { Suspense, lazy, useEffect, useState } from 'react';
import { NavLink, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router';
import { api, restoreSession, setSessionLostHandler, signOut, type Session } from './api.js';
import { Account } from './pages/Account.js';
import { ImportPage } from './pages/Import.js';
import { People } from './pages/People.js';
import { PersonCardPage } from './pages/PersonCard.js';
import { Recover } from './pages/Recover.js';
import { Setup } from './pages/Setup.js';
import { SignIn } from './pages/SignIn.js';
import { holds, type AbilityRule } from './permissions.js';

// development only, so it isn't in the build a church downloads
const Gallery = import.meta.env.DEV ? lazy(() => import('./pages/Gallery.js').then((m) => ({ default: m.Gallery }))) : null;

/** What `/api/me` says about who is signed in. The menu comes from the api lane, which knows the church's modules (design §6.2). */
interface Me {
  churchName: string | null;
  displayName: string | null;
  locale: string;
  terms: Record<string, string>;
  menu: { key: string; nameKey: string; route: string; icon: string; order: number }[];
  rules: AbilityRule[];
}

/** Who is signed in, and the app shell around what they can reach (design §7). */
export function App() {
  const [session, setSession] = useState<Session | null | undefined>(undefined);   // undefined: still asking the cookie
  const [grantId, setGrantId] = useState<string | null>(null);        // held in memory only, never in the address
  const [signInNotice, setSignInNotice] = useState<string | null>(null);
  const [me, setMe] = useState<Me | null>(null);
  const [translate, setTranslate] = useState<Translate>(() => createTranslator(sourceLocale));
  const navigate = useNavigate();
  const { pathname } = useLocation();

  useEffect(() => {
    setSessionLostHandler(() => { setSession(null); navigate('/sign-in'); });
    restoreSession().then(setSession);
  }, [navigate]);

  useEffect(() => {
    if (!session) { setMe(null); return; }
    api<Me>('/me')
      .then((answer) => { setMe(answer); setTranslate(() => createTranslator(isLocale(answer.locale) ? answer.locale : sourceLocale, answer.terms)); })
      .catch(() => setMe(null));
  }, [session]);

  const backToSignIn = () => {
    setGrantId(null);
    setSignInNotice('You’re all set. Sign in now. If you’ve just set up your authenticator app, wait for it to show a new code first.');
    navigate('/sign-in');
  };
  const startAgain = () => {
    setGrantId(null);
    setSignInNotice('That took a little too long. Please sign in again to carry on.');
    navigate('/sign-in');
  };

  if (session === undefined) return <main className="page-loading" role="status">One moment…</main>;

  if (!session) {
    return (
      <Routes>
        <Route path="/sign-in" element={<SignIn notice={signInNotice} onSignedIn={(s) => { setSignInNotice(null); setSession(s); navigate('/people'); }} onSetupRequired={(id) => { setGrantId(id); navigate('/setup'); }} />} />
        <Route path="/setup" element={grantId ? <Setup grantId={grantId} onDone={backToSignIn} onExpired={startAgain} /> : <Navigate to="/sign-in" replace />} />
        <Route path="/recover" element={<Recover onDone={backToSignIn} />} />
        <Route path="*" element={<Navigate to="/sign-in" replace />} />
      </Routes>
    );
  }

  const t = translate;
  const rules = me?.rules ?? [];
  // the server refuses the import to anyone else; this only keeps the menu honest
  const nav: ShellLink[] = [
    ...(me?.menu ?? []).map((m) => ({ key: m.key, label: t(m.nameKey), to: m.route, icon: m.icon })),
    ...(holds(rules, ['manage', 'run'], 'import') ? [{ key: 'import', label: t('shell.import'), to: '/import', icon: 'import' }] : []),
  ];
  const personalNav: ShellLink[] = [{ key: 'account', label: t('shell.account'), to: '/account', icon: 'lock' }];
  const title = [...nav, ...personalNav].find((n) => pathname === n.to || pathname.startsWith(`${n.to}/`))?.label ?? 'Trellistry';
  // the People screen has its own search, which this would only repeat
  const search = pathname === '/people' ? {} : { search: { label: t('shell.findLabel'), placeholder: t('shell.findPlaceholder'), onSubmit: (text: string) => navigate(text ? `/people?q=${encodeURIComponent(text)}` : '/people') } };

  return (
    <AppShell
      title={title}
      churchName={me?.churchName}
      nav={nav}
      personalNav={personalNav}
      LinkComponent={NavLink}
      user={me?.displayName ? { name: me.displayName, to: '/account' } : null}
      onSignOut={async () => { await signOut(); setSession(null); navigate('/sign-in'); }}
      words={{ skipToContent: t('shell.skipToContent'), mainMenu: t('shell.mainMenu'), more: t('shell.more'), moreMenu: t('shell.moreMenu'), close: t('shell.close'), signOut: t('shell.signOut') }}
      {...search}
    >
      <Suspense fallback={null}>
        <Routes>
          <Route path="/people" element={<People />} />
          <Route path="/people/:id" element={<PersonCardPage />} />
          <Route path="/import" element={<ImportPage />} />
          <Route path="/account" element={<Account mayHelpOthers={holds(rules, ['manage'], 'users')} />} />
          {Gallery && <Route path="/ui-gallery" element={<Gallery />} />}
          <Route path="*" element={<Navigate to="/people" replace />} />
        </Routes>
      </Suspense>
    </AppShell>
  );
}
